import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, Spinner, Table } from 'react-bootstrap';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { onErrorShowMessage } from '~src/errors';
import { fetchAllegatiWithAiFields } from '~graphql/controllers/allegati';
import {
  formatAiCreditsAmount,
  getAiCreditAttachmentPreview,
  getAiCreditOrderAddedAmount,
  getAiCreditOrderDetail,
  getAiCreditOrderFuneralService,
  getAiCreditOrderMovementLabel,
  getAiCreditOrderMovementType,
  getAiCreditOrderToolLabel,
  getAiCreditOrderUsedAmount,
} from '~src/utils/aiCreditsHistory';

const EMPTY_ATTACHMENT_PREVIEW = {
  unavailable: true,
};

const AttachmentPreviewModal = ({ loading, preview, show, onHide }) => {
  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Foto usata per questo credito</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="ai-credits-history__state">
            <Spinner animation="border" variant="info" />
            <span>Sto caricando le foto...</span>
          </div>
        ) : preview?.unavailable || !preview ? (
          <p className="ai-credits-history__empty-state mb-0">
            Non riesco a mostrare un confronto per questo allegato.
          </p>
        ) : (
          <div className="ai-credits-history__preview-grid">
            <div className="ai-credits-history__preview-card">
              <strong className="ai-credits-history__preview-title">
                Prima
              </strong>
              {preview.hasBefore ? (
                <div className="ai-credits-history__preview-media">
                  <img
                    src={preview.beforeUrl}
                    alt={`Prima - ${preview.title}`}
                  />
                </div>
              ) : (
                <p className="ai-credits-history__empty-state mb-0">
                  Il file originale non è disponibile.
                </p>
              )}
            </div>

            <div className="ai-credits-history__preview-card">
              <strong className="ai-credits-history__preview-title">
                Dopo
              </strong>
              <div className="ai-credits-history__preview-media">
                <img src={preview.afterUrl} alt={`Dopo - ${preview.title}`} />
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

AttachmentPreviewModal.propTypes = {
  loading: PropTypes.bool,
  onHide: PropTypes.func,
  preview: PropTypes.object,
  show: PropTypes.bool,
};

const CreditsOrders = (props) => {
  const [selectedAttachmentId, setSelectedAttachmentId] = useState('');
  const [loadingAttachmentId, setLoadingAttachmentId] = useState('');
  const [attachmentPreviews, setAttachmentPreviews] = useState({});

  const selectedAttachmentPreview = selectedAttachmentId
    ? attachmentPreviews[selectedAttachmentId]
    : null;

  const handleOpenAttachmentPreview = async (attachmentId) => {
    if (!attachmentId) {
      return;
    }

    setSelectedAttachmentId(attachmentId);

    if (attachmentPreviews[attachmentId]) {
      return;
    }

    setLoadingAttachmentId(attachmentId);

    try {
      const attachments = await fetchAllegatiWithAiFields(
        {
          objectId: {
            equalTo: attachmentId,
          },
        },
        [],
        1
      );
      const preview = getAiCreditAttachmentPreview(attachments[0]);

      setAttachmentPreviews((currentPreviews) => ({
        ...currentPreviews,
        [attachmentId]: preview || EMPTY_ATTACHMENT_PREVIEW,
      }));
    } catch (err) {
      console.error(err);
      setAttachmentPreviews((currentPreviews) => ({
        ...currentPreviews,
        [attachmentId]: EMPTY_ATTACHMENT_PREVIEW,
      }));
      onErrorShowMessage();
    } finally {
      setLoadingAttachmentId('');
    }
  };

  return (
    <>
      <Table responsive hover className="ai-credits-history__table mb-0">
        <thead>
          <tr>
            <th>Tipo</th>
            <th>Data</th>
            <th>Crediti</th>
            <th>Strumento AI</th>
            <th>Defunto</th>
            <th>Dettaglio</th>
          </tr>
        </thead>
        <tbody>
          {props.orders.map((order) => {
            const movementType = getAiCreditOrderMovementType(order);
            const detail = getAiCreditOrderDetail(order);
            const funeralService = getAiCreditOrderFuneralService(order);
            const amount =
              getAiCreditOrderAddedAmount(order) -
              getAiCreditOrderUsedAmount(order);
            const amountClassName =
              amount > 0
                ? 'ai-credits-history__amount ai-credits-history__amount--positive'
                : amount < 0
                ? 'ai-credits-history__amount ai-credits-history__amount--negative'
                : 'ai-credits-history__amount';

            return (
              <tr key={order.objectId}>
                <td>
                  <span
                    className={`ai-credits-history__badge ai-credits-history__badge--${movementType}`}
                  >
                    {getAiCreditOrderMovementLabel(order)}
                  </span>
                </td>
                <td>{format(parseISO(order.createdAt), 'dd/MM/yyyy HH:mm')}</td>
                <td className={amountClassName}>
                  {formatAiCreditsAmount(amount, { signed: true })}
                </td>
                <td>{getAiCreditOrderToolLabel(order)}</td>
                <td>{funeralService?.label || '—'}</td>
                <td>
                  <div className="ai-credits-history__detail-cell">
                    {detail.label && detail.href ? (
                      <a
                        data-navigo
                        href={detail.href}
                        className="ai-credits-history__detail-link"
                      >
                        {detail.label}
                      </a>
                    ) : detail.label ? (
                      <span>{detail.label}</span>
                    ) : null}

                    {detail.attachmentId ? (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="ai-credits-history__preview-link"
                        disabled={loadingAttachmentId === detail.attachmentId}
                        onClick={() =>
                          handleOpenAttachmentPreview(detail.attachmentId)
                        }
                      >
                        {loadingAttachmentId === detail.attachmentId
                          ? 'Carico foto...'
                          : 'Vedi prima e dopo'}
                      </Button>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <AttachmentPreviewModal
        loading={Boolean(
          selectedAttachmentId && loadingAttachmentId === selectedAttachmentId
        )}
        preview={selectedAttachmentPreview}
        show={Boolean(selectedAttachmentId)}
        onHide={() => setSelectedAttachmentId('')}
      />
    </>
  );
};

CreditsOrders.propTypes = {
  orders: PropTypes.array,
};

export default CreditsOrders;
